/**
 * Shared formatting and computation helpers for subscription UI components.
 *
 * Used by PaymentSummaryCard, PaymentHistoryCard, StudentCard, Success, and ViewSubscription pages.
 */

import { calculateTotalAdjustment, resolveInstallmentTxnIds, collectStampedTxnIds, type ScheduledInstallment, type Transaction, type TxnStamp } from '../api/payment';

export type { TxnStamp };

// ── Currency ──────────────────────────────────────────────────────────────────

/**
 * Format an amount (in rupees) as "₹X.XX" with Indian locale grouping and paise.
 * Returns "—" for null/undefined.
 */
export const formatCurrency = (amount: number | undefined | null): string => {
  if (amount == null) return '—';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ── Date / Time ───────────────────────────────────────────────────────────────

/**
 * Format an epoch-ms timestamp as "DD MMM YYYY" (e.g. "04 Mar 2026").
 * Returns "—" for falsy values.
 */
export const formatDateTime = (epochMs: number | undefined | null): string => {
  if (!epochMs) return '—';
  return new Date(epochMs).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Format a validity range as "DD MMM → DD MMM YYYY".
 * The year is shown only on the end date.
 * Example: "01 Jan → 30 Jun 2026"
 */
export const formatValidityRange = (
  validFrom: string | undefined | null,
  validUntil: string | undefined | null,
): string => {
  if (!validFrom || !validUntil) return '—';
  try {
    const from = new Date(validFrom + 'T00:00:00');
    const until = new Date(validUntil + 'T00:00:00');

    const fromStr = from.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    const untilStr = until.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

    return `${fromStr} → ${untilStr}`;
  } catch {
    return '—';
  }
};

/**
 * Net signed adjustment from a list of transactions.
 * Delegates to calculateTotalAdjustment so CREDIT/DEBIT + rounding stay consistent
 * across pay payload and display.
 */
export const netAdjustmentAmount = (txns: Transaction[]): number =>
  calculateTotalAdjustment(txns);

export const lookupTxnsByStamp = (
  record: TxnStamp,
  allTxnsById: Map<string, Transaction>,
): Transaction[] =>
  resolveInstallmentTxnIds(record)
    .map(id => allTxnsById.get(id))
    .filter((t): t is Transaction => !!t);

export const stampedAdjustmentAmount = (
  record: TxnStamp,
  allTxnsById: Map<string, Transaction>,
): number => netAdjustmentAmount(lookupTxnsByStamp(record, allTxnsById));

/** First PENDING/OVERDUE installment by due date (same object reference as input). */
export const firstPendingInstallment = <
  T extends { status?: string; paidAt?: number; dueDate?: string },
>(
  installments: T[],
): T | null =>
  [...installments]
    .filter(i => (i.status === 'PENDING' || i.status === 'OVERDUE') && !i.paidAt)
    .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''))[0] ?? null;

/** PENDING adjustments not yet stamped on a paid token/installment. */
export const unusedPendingAdjustments = (
  transactions: Transaction[],
  token?: TxnStamp | null,
  installments?: TxnStamp[] | null,
): Transaction[] => {
  const stamped = new Set(collectStampedTxnIds(token, installments));
  return transactions
    .filter(t => t.status === 'PENDING' && !stamped.has(t.id))
    .sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
};

// ── Payment Summary ───────────────────────────────────────────────────────────

export interface PaymentSummaryResult {
  totalPaid: number;
  totalSubscription: number;
  /** Effective amount of paid items after applying per-row adjustments (null if no difference). */
  adjustedPaid: number | null;
  /** Effective total of the whole subscription after all adjustments (null if no difference). */
  adjustedTotal: number | null;
  remaining: number;
  progressPct: number;
}

/**
 * Compute payment summary from token + installments.
 *
 * Matches o-refine-react subscription Payment Calendar behaviour:
 *  - Paid token/installments: apply stamped transactionIds (fallback transactionId)
 *  - Pending: apply unused PENDING adjustments to the first PENDING|OVERDUE installment
 *    (excludes collectStampedTxnIds — mirrors pay-calc / StudentCard)
 *
 * totalSubscription = calendarTotalAmount (already includes token + all installments)
 * totalPaid = sum of token (if paidAt set) + sum of PAID installment amounts
 * adjustedTotal = calendar total with unused PENDING adjustments applied to the
 * first PENDING/OVERDUE installment (CREDIT reduces, DEBIT increases)
 * remaining = totalSubscription − totalPaid (0 when fully paid)
 * progressPct = round((totalPaid / totalSubscription) × 100), clamped [0, 100]
 */
export const computePaymentSummary = (
  token: (TxnStamp & { amount?: number; paidAt?: number }) | null | undefined,
  calendarTotalAmount: number,
  installments: Array<
    Pick<ScheduledInstallment, 'amount' | 'status' | 'paidAt' | 'installmentNumber' | 'dueDate'> & TxnStamp
  >,
  transactions?: Transaction[],
): PaymentSummaryResult => {
  const tokenPaid = token?.paidAt ? (token.amount ?? 0) : 0;

  const paidInstallmentTotal = installments
    .filter(i => i.status === 'PAID' || !!i.paidAt)
    .reduce((sum, i) => sum + (i.amount ?? 0), 0);

  const totalPaid = tokenPaid + paidInstallmentTotal;
  const totalSubscription = calendarTotalAmount ?? 0;
  const progressPct =
    totalSubscription > 0
      ? Math.min(100, Math.max(0, Math.round((totalPaid / totalSubscription) * 100)))
      : 0;

  let adjustedTotal: number | null = null;
  let adjustedPaid: number | null = null;

  if (transactions && transactions.length > 0) {
    const pendingTxns = unusedPendingAdjustments(transactions, token, installments);
    const allTxnsById = new Map(transactions.map(t => [t.id, t]));
    const firstPending = firstPendingInstallment(installments);

    let tokenEffective = token?.amount ?? 0;
    if (token) {
      if (token.paidAt) {
        tokenEffective = (token.amount ?? 0) + stampedAdjustmentAmount(token, allTxnsById);
      } else if (!firstPending) {
        tokenEffective = (token.amount ?? 0) + netAdjustmentAmount(pendingTxns);
      }
    }

    const installmentsEffective = installments.reduce((sum, inst) => {
      const isPaid = inst.status === 'PAID' || !!inst.paidAt;
      if (isPaid) {
        return sum + (inst.amount ?? 0) + stampedAdjustmentAmount(inst, allTxnsById);
      }
      if (pendingTxns.length > 0 && firstPending && inst === firstPending) {
        return sum + (inst.amount ?? 0) + netAdjustmentAmount(pendingTxns);
      }
      return sum + (inst.amount ?? 0);
    }, 0);

    const computed = (token ? tokenEffective : 0) + installmentsEffective;
    if (computed !== totalSubscription) {
      adjustedTotal = computed;
    }

    let adjustedPaidTotal = token?.paidAt ? tokenEffective : 0;
    for (const inst of installments) {
      if (inst.status === 'PAID' || !!inst.paidAt) {
        adjustedPaidTotal += (inst.amount ?? 0) + stampedAdjustmentAmount(inst, allTxnsById);
      }
    }
    if (adjustedPaidTotal !== totalPaid) {
      adjustedPaid = adjustedPaidTotal;
    }
  }

  const remaining =
    adjustedTotal !== null && adjustedPaid !== null
      ? Math.max(0, adjustedTotal - adjustedPaid)
      : adjustedTotal !== null
        ? Math.max(0, adjustedTotal - totalPaid)
        : Math.max(0, totalSubscription - totalPaid);

  return { totalPaid, totalSubscription, adjustedPaid, adjustedTotal, remaining, progressPct };
};

// ── Just-Paid Identification ──────────────────────────────────────────────────

export type JustPaidResult =
  | { type: 'token'; amount: number; paidAt?: number }
  | {
      type: 'installment';
      installmentNumber: number;
      totalInstallments: number;
      amount: number;
      date?: string;
    }
  | { type: 'unknown' };

/**
 * Identify which payment was just completed.
 *
 * Checks token first, then installments. Exactly one of the three cases holds.
 */
export const identifyJustPaidPayment = (
  justPaidPaymentId: string | undefined | null,
  token: { paymentId?: string; amount?: number; paidAt?: number } | undefined | null,
  installments: ScheduledInstallment[],
): JustPaidResult => {
  if (!justPaidPaymentId) return { type: 'unknown' };

  if (token?.paymentId === justPaidPaymentId) {
    return { type: 'token', amount: token.amount ?? 0, paidAt: token.paidAt };
  }

  const match = installments.find(i => i.paymentId === justPaidPaymentId);
  if (match) {
    return {
      type: 'installment',
      installmentNumber: match.installmentNumber ?? 0,
      totalInstallments: installments.length,
      amount: match.amount ?? 0,
      date: match.dueDate,
    };
  }

  return { type: 'unknown' };
};

// ── Grade Formatting ──────────────────────────────────────────────────────────

/**
 * Format grade string: "GRADE_6" → "Grade 6", "CLASS_10" → "Class 10".
 */
export const formatGrade = (grade?: string): string => {
  if (!grade) return '';
  return grade
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
